import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, Users, MessageSquare, ArrowLeft } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Message } from '../../types/community';
import { MessageItem } from './MessageItem';
import { CommentSection } from './CommentSection';
import { formatTimeShort } from '../../utils/formatters';
import { parseThreadMetadata } from '../../lib/threadUtils';

interface MessagePermalinkProps {
  messageId?: string; // Optional prop to override URL param
}

export function MessagePermalink({ messageId: propMessageId }: MessagePermalinkProps) {
  const { messageId: urlMessageId } = useParams<{ messageId: string }>();
  const messageId = propMessageId || urlMessageId;
  const navigate = useNavigate();
  
  const [message, setMessage] = useState<Message | null>(null);
  const [replies, setReplies] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Fetch current user
  useEffect(() => {
    const fetchCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        
        setCurrentUser(user);
        setIsAdmin(profile?.is_admin || false);
      }
    };
    
    fetchCurrentUser();
  }, []);

  // Fetch message and its replies
  useEffect(() => {
    if (!messageId) return;

    const fetchMessageAndReplies = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch the main message
        const { data: messageData, error: messageError } = await supabase
          .from('messages')
          .select(`
            *,
            profiles:user_id (
              email,
              display_name,
              avatar_url,
              is_admin
            ),
            comments:comments (
              id,
              content,
              created_at,
              user_id,
              profiles:user_id (
                email,
                display_name,
                avatar_url,
                is_admin
              )
            )
          `)
          .eq('id', messageId)
          .single();

        if (messageError) {
          console.error('Error fetching message:', messageError);
          setError('Message not found');
          return;
        }

        // Transform the data to match our Message type and clean thread metadata
        const threadedMessage = parseThreadMetadata(messageData);
        const transformedMessage: Message = {
          ...messageData,
          content: threadedMessage.content, // Use cleaned content
          like_count: messageData.like_count || 0,
          user_has_liked: false, // We'll fetch this separately if needed
          comments: messageData.comments || []
        };

        setMessage(transformedMessage);

        // Fetch replies to this message using thread metadata approach
        const { data: repliesData, error: repliesError } = await supabase
          .from('messages')
          .select(`
            *,
            profiles:user_id (
              email,
              display_name,
              avatar_url,
              is_admin
            ),
            comments:comments (
              id,
              content,
              created_at,
              user_id,
              profiles:user_id (
                email,
                display_name,
                avatar_url,
                is_admin
              )
            )
          `)
          .like('content', `%[reply:${messageId}]%`)
          .order('created_at', { ascending: true });

        if (repliesError) {
          console.error('Error fetching replies:', repliesError);
        } else {
          const transformedReplies = (repliesData || []).map(reply => {
            const threadedReply = parseThreadMetadata(reply);
            return {
              ...reply, // Keep all original message properties
              content: threadedReply.content, // Use cleaned content from parseThreadMetadata
              like_count: reply.like_count || 0,
              user_has_liked: false,
              comments: reply.comments || []
            };
          });
          console.log('Fetched replies for message:', messageId, 'Replies:', transformedReplies);
          setReplies(transformedReplies);
        }

      } catch (err) {
        console.error('Error in fetchMessageAndReplies:', err);
        setError('Failed to load message');
      } finally {
        setLoading(false);
      }
    };

    fetchMessageAndReplies();
  }, [messageId]);

  const handleBack = () => {
    navigate('/community');
  };

  const handleTogglePin = async (msgId: string, isPinned: boolean) => {
    try {
      const { error } = await supabase
        .from('messages')
        .update({ is_pinned: !isPinned })
        .eq('id', msgId);

      if (error) throw error;

      // Update local state
      if (message && message.id === msgId) {
        setMessage({ ...message, is_pinned: !isPinned });
      }
      
      setReplies(replies => replies.map(reply => 
        reply.id === msgId ? { ...reply, is_pinned: !isPinned } : reply
      ));
    } catch (err) {
      console.error('Error toggling pin:', err);
    }
  };

  const handleDelete = async (msgId: string, userId: string) => {
    try {
      const { error } = await supabase
        .from('messages')
        .delete()
        .eq('id', msgId);

      if (error) throw error;

      // If main message is deleted, navigate back
      if (message && message.id === msgId) {
        navigate('/community');
        return;
      }

      // Remove from replies
      setReplies(replies => replies.filter(reply => reply.id !== msgId));
    } catch (err) {
      console.error('Error deleting message:', err);
    }
  };

  const handleAddComment = async (msgId: string, content: string): Promise<boolean> => {
    try {
      if (!currentUser) return false;

      const { data, error } = await supabase
        .from('comments')
        .insert({
          message_id: msgId,
          user_id: currentUser.id,
          content: content.trim()
        })
        .select(`
          *,
          profiles:user_id (
            email,
            display_name,
            avatar_url,
            is_admin
          )
        `)
        .single();

      if (error) throw error;

      // Update local state
      const newComment = {
        ...data,
        profiles: data.profiles
      };

      if (message && message.id === msgId) {
        setMessage({
          ...message,
          comments: [...(message.comments || []), newComment]
        });
      } else {
        setReplies(replies => replies.map(reply => 
          reply.id === msgId 
            ? { ...reply, comments: [...(reply.comments || []), newComment] }
            : reply
        ));
      }

      return true;
    } catch (err) {
      console.error('Error adding comment:', err);
      return false;
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 py-4 px-2 sm:p-8 md:pl-24 flex flex-col w-full overflow-hidden" style={{ height: '100dvh' }}>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-[#151D29]/60 rounded-lg w-1/4"></div>
          <div className="h-32 bg-[#151D29]/60 rounded-lg"></div>
          <div className="h-24 bg-[#151D29]/60 rounded-lg ml-8"></div>
          <div className="h-20 bg-[#151D29]/60 rounded-lg ml-16"></div>
        </div>
      </div>
    );
  }

  if (error || !message) {
    return (
      <div className="fixed inset-0 py-4 px-2 sm:p-8 md:pl-24 flex flex-col w-full overflow-hidden" style={{ height: '100dvh' }}>
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-[#c9fffc] hover:text-white transition-colors mb-6 group"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          Back to Community
        </button>
        
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6 text-center">
          <h2 className="text-xl font-bold text-red-400 mb-2">Message Not Found</h2>
          <p className="text-red-300">{error || 'The message you\'re looking for doesn\'t exist or has been deleted.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 py-4 px-2 sm:p-8 md:pl-24 flex flex-col w-full overflow-hidden" style={{ height: '100dvh' }}>
      {/* Header with back button */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-8"
      >
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-white text-sm bg-[#151D29]/60 backdrop-blur-md px-4 py-2 rounded-lg border border-[#c9fffc]/10 shadow-md hover:border-[#c9fffc]/30 hover:shadow-[#c9fffc]/10 transition-all duration-300"
        >
          <ArrowLeft className="w-5 h-5 text-[#c9fffc]" />
          Back to Community
        </button>
        
        <div className="text-sm text-gray-400">
          Message • {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
        </div>
      </motion.div>

      <div className="overflow-y-auto overflow-x-hidden hide-scrollbar flex-1 flex-grow pb-24 max-w-full w-full relative z-20" style={{ height: 'calc(100dvh - 10rem)' }}>
        {/* Main message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <MessageItem
            message={message}
            isAdmin={isAdmin}
            currentUserId={currentUser?.id}
            onTogglePin={handleTogglePin}
            onDelete={handleDelete}
            onAddComment={handleAddComment}
          />
        </motion.div>

        {/* Replies */}
        {replies.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="space-y-4"
          >
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-[#c9fffc]" />
              Replies ({replies.length})
            </h3>
            
            <div className="space-y-4 pl-8 border-l-2 border-[#c9fffc]/20">
              {replies.map((reply, index) => (
                <motion.div
                  key={reply.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * index }}
                >
                  <MessageItem
                    message={reply}
                    isAdmin={isAdmin}
                    currentUserId={currentUser?.id}
                    onTogglePin={handleTogglePin}
                    onDelete={handleDelete}
                    onAddComment={handleAddComment}
                  />
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}


      </div>
    </div>
  );
} 